/**
 * columnPreferences.js
 * Reusable localStorage helpers for table column visibility preferences.
 * TTL: 30 days
 */

export const COLUMN_PREF_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export const COLUMN_PREF_PAGES = {
    PRODUCTS: 'products',
    ORDER_ITEMS: 'order-items',
    EXPENSES: 'expenses',
};

/**
 * @param {string} pageId
 * @param {string|number|null|undefined} userId
 * @returns {string}
 */
export const buildColumnPreferenceKey = (pageId, userId) =>
    `column_prefs-${pageId}-${userId ?? 'anonymous'}`;

const isExpired = (timestamp) => {
    if (!timestamp || typeof timestamp !== 'number') return true;
    return Date.now() - timestamp > COLUMN_PREF_TTL_MS;
};

/**
 * Read saved toggleable column keys. Returns null when missing, expired, or corrupt.
 * @param {string} key
 * @returns {string[]|null}
 */
export const getColumnPreferences = (key) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            localStorage.removeItem(key);
            return null;
        }

        if (isExpired(parsed.timestamp)) {
            localStorage.removeItem(key);
            return null;
        }

        if (!Array.isArray(parsed.data)) {
            localStorage.removeItem(key);
            return null;
        }

        return parsed.data;
    } catch {
        try { localStorage.removeItem(key); } catch { /* ignore */ }
        return null;
    }
};

/**
 * Persist toggleable column keys with a timestamp for TTL checks.
 * @param {string} key
 * @param {string[]} toggleableKeys
 * @returns {boolean}
 */
export const setColumnPreferences = (key, toggleableKeys) => {
    try {
        const entry = { data: toggleableKeys, timestamp: Date.now() };
        localStorage.setItem(key, JSON.stringify(entry));
        return true;
    } catch {
        return false;
    }
};

/**
 * Merge saved preferences (or defaults) into a full visible-columns list.
 *
 * @param {object} params
 * @param {string[]} params.allKeys
 * @param {string[]} params.fixedKeys
 * @param {Set<string>} params.hiddenByDefaultKeys
 * @param {string[]|null} params.savedToggleableKeys - null means no saved pref
 * @returns {string[]}
 */
export const resolveVisibleColumns = ({
    allKeys,
    fixedKeys,
    hiddenByDefaultKeys,
    savedToggleableKeys,
}) => {
    const fixedSet = new Set(fixedKeys);
    const allKeysSet = new Set(allKeys);

    if (savedToggleableKeys !== null) {
        const validSaved = savedToggleableKeys.filter(
            (k) => allKeysSet.has(k) && !fixedSet.has(k)
        );
        return [...fixedKeys, ...validSaved];
    }

    return allKeys.filter((k) => !hiddenByDefaultKeys.has(k));
};
